import { useEffect, useRef } from "react";

interface ModalEntry {
	close: () => void;
	element: () => HTMLElement | null;
}

interface ModalState {
	entries: ModalEntry[];
	overflow: string;
	onKeyDown: (event: KeyboardEvent) => void;
}

const modalDocuments = new WeakMap<Document, ModalState>();

/** Share keyboard ownership and the scroll lock across every modal host. */
export const registerModal = (entry: ModalEntry, owner: Document = document) => {
	let state = modalDocuments.get(owner);
	if (!state) {
		const entries: ModalEntry[] = [];
		state = {
			entries,
			overflow: owner.body.style.overflow,
			onKeyDown: event => {
				if (event.key !== "Escape" || event.defaultPrevented) return;
				let top = entries.at(-1);
				// Passive effects mount children before parents. A newly mounted parent
				// must not steal Escape from a dialog inside its own DOM subtree.
				for (let index = entries.length - 2; index >= 0; index--) {
					const candidate = entries[index];
					const child = candidate.element();
					if (child && top?.element()?.contains(child)) top = candidate;
				}
				if (!top) return;
				event.preventDefault();
				event.stopImmediatePropagation();
				top.close();
			},
		};
		modalDocuments.set(owner, state);
		owner.body.style.overflow = "hidden";
		// Let React controls and nested portals consume Escape before the dialog.
		owner.addEventListener("keydown", state.onKeyDown);
	}
	state.entries.push(entry);
	const registeredState = state;
	return () => {
		const index = registeredState.entries.indexOf(entry);
		if (index === -1) return;
		registeredState.entries.splice(index, 1);
		if (registeredState.entries.length > 0) return;
		owner.body.style.overflow = registeredState.overflow;
		owner.removeEventListener("keydown", registeredState.onKeyDown);
		modalDocuments.delete(owner);
	};
};

export const useModalLayer = (enabled: boolean, onClose?: () => void) => {
	const close = useRef(onClose);
	const element = useRef<HTMLDivElement | null>(null);
	close.current = onClose;
	useEffect(() => {
		if (!enabled) return;
		return registerModal({ close: () => close.current?.(), element: () => element.current });
	}, [enabled]);
	return element;
};
